import { Box, FormControl, MenuItem, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import colors from '../../constants/colors'
import { IIconDropdown } from './types'

export function useComponentVisible(initialIsVisible: boolean) {
  const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible)
  const ref = useRef<any>(null)

  const handleHideDropdown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsComponentVisible(false)
    }
  }

  const handleClickOutside = (event: any) => {
    if (ref?.current && !ref?.current?.contains(event.target)) {
      setIsComponentVisible(false)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleHideDropdown, true)
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('keydown', handleHideDropdown, true)
      document.removeEventListener('click', handleClickOutside, true)
    }
  })

  return { ref, isComponentVisible, setIsComponentVisible }
}

const containerStyle = {
  position: 'fixed',
  zIndex: 999999,
  backgroundColor: colors.WHITE,
  border: `1px solid ${colors.TAG_BORDER_BLUE}`,
  borderRadius: '.5rem',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 0px 2px rgba(190, 193, 202, 0.2), 0px 2px 10px rgba(47, 52, 59, 0.1)',
}

const IconDropdown: React.FC<IIconDropdown> = ({
  items = [],
  icon,
  containerSx,
  iconWidth = 24,
  iconHeight = 24,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [displayAtTop, setDisplayAtTop] = useState(false)
  const [moveFromBottom, setMoveFromBottom] = useState<number>(0)
  const dropdownRef = useRef()
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false)

  const onIconClick = () => {
    setIsOpen(!isOpen)
    setIsComponentVisible(!isOpen)
  }

  const checkVisible = (elm: { getBoundingClientRect: () => any }) => {
    const rect = elm?.getBoundingClientRect()
    const iconRec = ref?.current?.getBoundingClientRect()
    setMoveFromBottom(Math.floor(window.innerHeight - iconRec?.y))
    setDisplayAtTop(rect?.y + rect?.height > window.innerHeight)
  }

  useEffect(() => {
    if (isOpen && dropdownRef && dropdownRef?.current) {
      checkVisible(dropdownRef?.current)
    }
    dropdownRef.current = undefined
  }, [isOpen])

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        width: iconWidth,
        height: iconHeight,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...containerSx,
      }}
      id='icondropdown'
      ref={ref}
    >
      <FormControl fullWidth sx={{ width: '100%', height: '100%' }}>
        <Box role='button' onClick={onIconClick}>
          {icon}
        </Box>
        {isComponentVisible && isOpen && (
          <Box
            sx={{
              ...containerStyle,
              ...(displayAtTop ? { bottom: `${moveFromBottom}px` } : {}),
            }}
            ref={dropdownRef}
          >
            {items.length &&
              items?.map((item, index) => (
                <MenuItem onClick={item.onClick} key={`${item.name}+${index}`}>
                  <Typography color='inherit' variant='bodyRegular' sx={{ paddingRight: '10rem' }}>
                    {' '}
                    {item.name}
                  </Typography>
                </MenuItem>
              ))}
          </Box>
        )}
      </FormControl>
    </Box>
  )
}

export default IconDropdown
